import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  listProjects,
  listProjectsPaginated,
  createProject,
  deleteProject,
} from '../../api/projects';
import { fetchHierarchy } from '../../api/analytics';
import type { HierarchyNode, HierarchyResponse } from '../../api/analytics';
import { OrgStatusCard } from '../dashboard/OrgStatusCard';
import { ProjectForm } from './ProjectForm';
import { Pagination } from '../shared/Pagination';
import { fmt, fmtCHF } from '../../utils/format';
import { getCantonName } from '../../utils/cantonData';
import { toast } from '../../stores/uiStore';
import type { GrandProjet } from '../../types/estimation';
import { useTranslation } from 'react-i18next';
import styles from './ProjectListPage.module.css';

type ViewMode = 'all' | 'hierarchy' | 'org-projects';

interface BreadcrumbItem {
  id: string | null;
  name: string;
}

function childLevelOf(level: string): string {
  if (level === 'federal') return 'canton';
  if (level === 'canton') return 'commune';
  return '';
}

function badgeClass(level: string): string {
  if (level === 'federal') return styles.badgeFederal ?? '';
  if (level === 'canton') return styles.badgeCanton ?? '';
  if (level === 'commune') return styles.badgeCommune ?? '';
  return '';
}

export function ProjectListPage() {
  const { t, i18n } = useTranslation('common');
  const navigate = useNavigate();

  function levelLabel(level: string): string {
    if (level === 'federal') return t('federal');
    if (level === 'canton') return t('cantons');
    if (level === 'commune') return t('communes');
    return level;
  }

  // All projects (unfiltered)
  const [allProjects, setAllProjects] = useState<GrandProjet[]>([]);

  // Hierarchy state
  const [hierarchy, setHierarchy] = useState<HierarchyResponse | null>(null);
  const [currentNodes, setCurrentNodes] = useState<HierarchyNode[]>([]);
  const [selectedNode, setSelectedNode] = useState<HierarchyNode | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([
    { id: null, name: t('projects') },
  ]);
  const [viewMode, setViewMode] = useState<ViewMode>('all');

  // Filtered projects for org-projects view
  const [orgProjects, setOrgProjects] = useState<GrandProjet[]>([]);
  const [loading, setLoading] = useState(true);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Create modal state
  const [showCreate, setShowCreate] = useState(false);

  // Filter state for "all" view
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(50);
  const [totalProjects, setTotalProjects] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  // Close modal on Escape
  useEffect(() => {
    if (!showCreate) return;
    function handleEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setShowCreate(false);
    }
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [showCreate]);

  async function loadInitialData() {
    try {
      setLoading(true);
      setError(null);
      // Load all projects and hierarchy in parallel
      const [projectsData, hierarchyData] = await Promise.all([
        listProjectsPaginated({ is_custom: true, page: 1, limit: pageLimit }),
        fetchHierarchy().catch(() => null), // hierarchy is optional
      ]);
      setAllProjects(projectsData.items);
      setTotalProjects(projectsData.total);
      setTotalPages(projectsData.pages);
      setPage(1);
      if (hierarchyData) setHierarchy(hierarchyData);
      setViewMode('all');
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('failedToLoadData');
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  async function loadProjectsPage(pg: number, lim: number, query: string) {
    try {
      setProjectsLoading(true);
      const data = await listProjectsPaginated({
        is_custom: true,
        page: pg,
        limit: lim,
        q: query || undefined,
      });
      setAllProjects(data.items);
      setTotalProjects(data.total);
      setTotalPages(data.pages);
      setPage(data.page);
    } catch (err) {
      console.error('Failed to load projects page:', err);
    } finally {
      setProjectsLoading(false);
    }
  }

  // Build org lookup from hierarchy for displaying org names on project cards
  const orgLookup = useMemo(() => {
    const map = new Map<string, { name: string; level: string; code: string }>();
    if (!hierarchy) return map;
    function walk(nodes: HierarchyNode[]) {
      for (const n of nodes) {
        map.set(n.id, { name: n.name, level: n.level, code: n.code });
        walk(n.children);
      }
    }
    walk(hierarchy.roots);
    return map;
  }, [hierarchy]);

  /** Resolve display name: use localized canton name for canton-level orgs */
  function displayName(node: { name: string; level: string; code: string }): string {
    if (node.level === 'canton' && node.code) {
      return getCantonName(node.code, i18n.language);
    }
    return node.name;
  }

  /** Resolve breadcrumb display name via orgLookup */
  function crumbDisplayName(crumb: BreadcrumbItem): string {
    if (!crumb.id) return crumb.name;
    const org = orgLookup.get(crumb.id);
    return org ? displayName(org) : crumb.name;
  }

  // Server-side search with debounce
  function handleSearchChange(value: string) {
    setSearchQuery(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setPage(1);
      loadProjectsPage(1, pageLimit, value);
    }, 300);
  }

  function handlePageChange(newPage: number) {
    setPage(newPage);
    loadProjectsPage(newPage, pageLimit, searchQuery);
  }

  function handleLimitChange(newLimit: number) {
    setPageLimit(newLimit);
    setPage(1);
    loadProjectsPage(1, newLimit, searchQuery);
  }

  // For display, allProjects is already the server-filtered page
  const filteredProjects = allProjects;

  function switchToHierarchy() {
    if (!hierarchy) return;
    setCurrentNodes(hierarchy.roots);
    setSelectedNode(null);
    setOrgProjects([]);
    setBreadcrumbs([{ id: null, name: t('projects') }]);
    setViewMode('hierarchy');
  }

  function switchToAll() {
    setViewMode('all');
    setSelectedNode(null);
    setBreadcrumbs([{ id: null, name: t('projects') }]);
  }

  const handleDrillDown = useCallback(async (node: HierarchyNode) => {
    if (node.children.length > 0) {
      // Has children — show hierarchy but also load this node's own direct projects
      setSelectedNode(node);
      setCurrentNodes(node.children);
      setBreadcrumbs((prev) => [...prev, { id: node.id, name: node.name }]);
      setViewMode('hierarchy');
      // Load projects assigned directly to this org (e.g. canton-level projects)
      try {
        const data = await listProjects({ organization_id: node.id });
        setOrgProjects(data);
      } catch {
        setOrgProjects([]);
      }
    } else {
      // Leaf node — load its projects
      setProjectsLoading(true);
      try {
        const data = await listProjects({ organization_id: node.id });
        setOrgProjects(data);
        setSelectedNode(node);
        setBreadcrumbs((prev) => [...prev, { id: node.id, name: node.name }]);
        setViewMode('org-projects');
      } catch {
        setError(t('failedToLoad'));
      } finally {
        setProjectsLoading(false);
      }
    }
  }, []);

  const handleBreadcrumbClick = useCallback((index: number) => {
    if (!hierarchy) return;
    if (index === 0) {
      switchToHierarchy();
    } else {
      const newBreadcrumbs = breadcrumbs.slice(0, index + 1);
      setBreadcrumbs(newBreadcrumbs);

      let nodes = hierarchy.roots;
      let selected: HierarchyNode | null = null;
      for (let i = 1; i < newBreadcrumbs.length; i++) {
        const crumb = newBreadcrumbs[i]!;
        const found = nodes.find((n) => n.id === crumb.id);
        if (found) {
          selected = found;
          nodes = found.children;
        }
      }
      setSelectedNode(selected);
      setCurrentNodes(nodes);
      setViewMode('hierarchy');
      setOrgProjects([]);
    }
  }, [hierarchy, breadcrumbs]);

  async function handleCreateProject(data: Record<string, unknown>) {
    const project = await createProject(data as Partial<GrandProjet> & { name: string; change_summary?: string });
    setShowCreate(false);
    navigate(`/app/projects/${project.id}`);
  }

  async function handleDelete(id: string) {
    if (!confirm(t('deleteConfirm'))) return;
    try {
      await deleteProject(id);
      toast.success(t('projectDeleted'));
      // Reload
      if (viewMode === 'org-projects' && selectedNode) {
        const data = await listProjects({ organization_id: selectedNode.id });
        setOrgProjects(data);
      }
      await loadProjectsPage(page, pageLimit, searchQuery);
    } catch (err) {
      console.error('Failed to delete project:', err);
      toast.error(t('failedToDeleteProject'));
    }
  }

  // --- Helpers ---

  function getOrgLabel(orgId: string | null | undefined): string | null {
    if (!orgId) return null;
    const org = orgLookup.get(orgId);
    if (!org) return null;
    return displayName(org);
  }

  const childLevel = selectedNode ? childLevelOf(selectedNode.level) : '';
  const childLevelStr = childLevel ? levelLabel(childLevel) : '';

  // --- Render ---

  if (loading) return <div className={styles.loading}>{t('loadingProjects')}</div>;
  if (error) return <div className={styles.loading} style={{ color: 'var(--color-danger, #e74c3c)' }}>{error}</div>;

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <h2>{t('projects')}</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {hierarchy && (
            <div className={styles.viewToggle}>
              <button
                className={`${styles.toggleBtn} ${viewMode === 'all' ? styles.toggleActive : ''}`}
                onClick={switchToAll}
              >
                {t('all')}
              </button>
              <button
                className={`${styles.toggleBtn} ${viewMode !== 'all' ? styles.toggleActive : ''}`}
                onClick={switchToHierarchy}
              >
                {t('byOrg')}
              </button>
            </div>
          )}
          <button className={styles.createBtn} onClick={() => setShowCreate(true)}>
            + {t('newProject')}
          </button>
        </div>
      </div>

      {/* ALL PROJECTS VIEW */}
      {viewMode === 'all' && (
        <>
          {/* Search bar */}
          <div className={styles.searchBar}>
            <input
              type="text"
              placeholder={t('searchProjects')}
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              className={styles.searchInput}
            />
            <span className={styles.projectCount}>
              {totalProjects} {t('projects')}
            </span>
          </div>

          {projectsLoading ? (
            <div className={styles.loading}>{t('loadingProjects')}</div>
          ) : filteredProjects.length === 0 ? (
            <div className={styles.empty}>
              {searchQuery ? t('noResults') : t('noProjects')}
            </div>
          ) : (
            <>
              <div className={styles.grid}>
                {filteredProjects.map((p) => renderProjectCard(p))}
              </div>
              <Pagination
                page={page}
                pages={totalPages}
                total={totalProjects}
                limit={pageLimit}
                onPageChange={handlePageChange}
                onLimitChange={handleLimitChange}
              />
            </>
          )}
        </>
      )}

      {/* HIERARCHY VIEW: org cards */}
      {viewMode === 'hierarchy' && (
        <>
          {/* Breadcrumb */}
          {breadcrumbs.length > 1 && (
            <div className={styles.breadcrumb}>
              {breadcrumbs.map((crumb, i) => (
                <span key={crumb.id ?? 'root'}>
                  {i > 0 && <span className={styles.breadcrumbSep}>/</span>}
                  {i < breadcrumbs.length - 1 ? (
                    <button
                      className={styles.breadcrumbItem}
                      onClick={() => handleBreadcrumbClick(i)}
                    >
                      {crumbDisplayName(crumb)}
                    </button>
                  ) : (
                    <span className={styles.breadcrumbCurrent}>
                      {crumbDisplayName(crumb)}
                    </span>
                  )}
                </span>
              ))}
            </div>
          )}

          {/* Selected node detail panel */}
          {selectedNode && (
            <div className={styles.detailPanel}>
              <div className={styles.detailHeader}>
                <span className={styles.detailName}>{displayName(selectedNode)}</span>
                <span className={`${styles.detailBadge} ${badgeClass(selectedNode.level)}`}>
                  {selectedNode.level}
                </span>
              </div>
              <div className={styles.detailStats}>
                <div className={styles.detailStat}>
                  <span className={styles.detailStatValue}>{fmt(selectedNode.total_logements)}</span>
                  <span className={styles.detailStatLabel}>{t('logements')}</span>
                </div>
                <div className={styles.detailStat}>
                  <span className={styles.detailStatValue}>{fmt(selectedNode.total_emplois)}</span>
                  <span className={styles.detailStatLabel}>{t('emplois')}</span>
                </div>
                <div className={styles.detailStat}>
                  <span className={styles.detailStatValue}>{selectedNode.project_count}</span>
                  <span className={styles.detailStatLabel}>{t('projects')}</span>
                </div>
                <div className={styles.detailStat}>
                  <span className={styles.detailStatValue}>
                    {selectedNode.total_cost > 0 ? fmtCHF(selectedNode.total_cost) : '\u2014'}
                  </span>
                  <span className={styles.detailStatLabel}>{t('investment')}</span>
                </div>
              </div>
            </div>
          )}

          {currentNodes.length > 0 && (
            <div className={styles.childrenLabel}>
              {selectedNode
                ? `${childLevelStr} (${currentNodes.length})`
                : `${t('organizations')} (${currentNodes.length})`
              }
            </div>
          )}
          <div className={styles.orgCardsGrid}>
            {currentNodes.map((node) => (
              <OrgStatusCard key={node.id} node={node} onClick={handleDrillDown} />
            ))}
          </div>
          {currentNodes.length === 0 && orgProjects.length === 0 && (
            <div className={styles.empty}>{t('noData')}</div>
          )}

          {/* Projects assigned directly to this org (not to a child) */}
          {selectedNode && orgProjects.length > 0 && (
            <>
              <div className={styles.childrenLabel} style={{ marginTop: 20 }}>
                {t('orgProjects', { name: displayName(selectedNode), count: orgProjects.length })}
              </div>
              <div className={styles.grid}>
                {orgProjects.map((p) => renderProjectCard(p))}
              </div>
            </>
          )}
        </>
      )}

      {/* ORG-PROJECTS VIEW: projects for a specific org */}
      {viewMode === 'org-projects' && (
        <>
          {/* Breadcrumb */}
          <div className={styles.breadcrumb}>
            {breadcrumbs.map((crumb, i) => (
              <span key={crumb.id ?? 'root'}>
                {i > 0 && <span className={styles.breadcrumbSep}>/</span>}
                {i < breadcrumbs.length - 1 ? (
                  <button
                    className={styles.breadcrumbItem}
                    onClick={() => handleBreadcrumbClick(i)}
                  >
                    {crumbDisplayName(crumb)}
                  </button>
                ) : (
                  <span className={styles.breadcrumbCurrent}>
                    {crumbDisplayName(crumb)}
                  </span>
                )}
              </span>
            ))}
          </div>

          {/* Org summary */}
          {selectedNode && (
            <div className={styles.orgSummary}>
              <span className={styles.orgSummaryName}>{displayName(selectedNode)}</span>
              <span className={styles.orgSummaryMeta}>
                {orgProjects.length} {t('projects')}
                {selectedNode.total_logements > 0 && <> &middot; {fmt(selectedNode.total_logements)} {t('logements')}</>}
              </span>
            </div>
          )}

          {projectsLoading ? (
            <p className={styles.loading}>{t('loadingProjects')}</p>
          ) : orgProjects.length === 0 ? (
            <div className={styles.empty}>{t('noProjects')}</div>
          ) : (
            <div className={styles.grid}>
              {orgProjects.map((p) => renderProjectCard(p))}
            </div>
          )}
        </>
      )}

      {/* Create project modal */}
      {showCreate && (
        <div className={styles.createOverlay} onClick={() => setShowCreate(false)}>
          <div className={styles.createDialog} style={{ maxWidth: 680, maxHeight: '90vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="create-project-title">
            <h3 id="create-project-title">{t('newProject')}</h3>
            <ProjectForm
              onSubmit={handleCreateProject}
              onCancel={() => setShowCreate(false)}
            />
          </div>
        </div>
      )}
    </div>
  );

  function renderProjectCard(p: GrandProjet) {
    const orgName = getOrgLabel(p.organization_id);

    return (
      <div
        key={p.id}
        className={styles.card}
        onClick={() => navigate(`/app/projects/${p.id}`)}
      >
        {orgName && (
          <div className={styles.cardOrg}>{orgName}</div>
        )}
        <h3 className={styles.cardTitle}>{p.name}</h3>
        <span className={styles.cardType}>{p.type}</span>
        <div className={styles.cardMeta}>
          {p.ha_total != null && <span>{p.ha_total} {t('ha')}</span>}
          <span>{p.logements} {t('logements')}</span>
          <span>{p.emplois} {t('emplois')}</span>
          {p.horizon_years && <span>{p.horizon_years} {t('ans')}</span>}
        </div>
        {(p.ha_industriel ?? 0) > 0 && (
          <div className={styles.cardMeta} style={{ marginTop: 2 }}>
            <span>ZI: {p.ha_industriel} {t('ha')}</span>
          </div>
        )}
        {p.communes.length > 0 && (
          <div className={styles.cardCommunes}>{p.communes.join(', ')}</div>
        )}
        <div className={styles.cardActions}>
          {p.is_custom && (
            <button
              className={`${styles.actionBtn} ${styles.dangerBtn}`}
              onClick={(e) => { e.stopPropagation(); handleDelete(p.id); }}
            >
              {t('delete')}
            </button>
          )}
        </div>
      </div>
    );
  }
}
